<template>
  <div ref="containerRef" class="shared-user-select">
    <!-- 
    Element plus 2.9.8 中新引入的 trigger-keys 
    默认为 ['Enter','Space'] 会导致在输入中文的时候按空格或者回车关闭掉弹出框
    当通过鼠标点击使触发元素获得焦点时，可以定义一组键盘按键代码，通过键盘控制气泡框的显示，在受控模式下无效。
    -->
    <el-popover
      placement="bottom-start"
      popper-class="shared-user-select__popper"
      :show-arrow="false"
      trigger="manual"
      :trigger-keys="[]"
      :visible="visible"
      :width="popoverWidth"
      @update:visible="visible = $event"
    >
      <div v-loading="searchLoading" class="shared-user-select__dropdown">
        <el-scrollbar v-if="userList.length" height="256px">
          <button
            v-for="item in userList"
            :key="item.userId || item.id"
            class="shared-user-select__option"
            type="button"
            @click="handleSelectUser(item)"
          >
            <img
              v-if="item.avatar && !item.avatar.includes('default.jpg')"
              :alt="item.name"
              class="shared-user-select__avatar"
              :src="item.avatar"
            />
            <img v-else alt="" class="shared-user-select__avatar" :src="userDefault" />
            <span class="shared-user-select__name">
              <template v-for="(part, index) in highlightName(item.name)" :key="index">
                <mark v-if="part.highlighted">{{ part.text }}</mark>
                <span v-else>{{ part.text }}</span>
              </template>
            </span>
          </button>
        </el-scrollbar>

        <div v-else class="shared-user-select__empty">
          <span>未找到搜索结果</span>
          <span>请尝试调整关键词</span>
        </div>
      </div>

      <template #reference>
        <el-input
          v-model="inputValue"
          clearable
          :placeholder="placeholder"
          :prefix-icon="Search"
          @clear="handleClearSearch"
          @click.stop="handleInputClick"
          @input="handleSearchInput"
        />
      </template>
    </el-popover>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue'
import { Search } from '@element-plus/icons-vue'
import { geuUserList } from '@/api/ai-board'
import userDefault from '@/assets/user-default.png'

defineOptions({
  name: 'SharedUserSelect'
})

const props = defineProps({
  departmentId: {
    type: [Number, String],
    default: ''
  },
  isReview: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: '请输入员工名称'
  }
})

const selectedValue = defineModel('value', {
  type: [Number, String],
  default: ''
})

const emits = defineEmits(['selectUser'])

const containerRef = ref(null)
const visible = ref(false)
// 自动计算的宽度
const popoverWidth = ref()
// 选中员工后需要保留为员工姓名
const inputValue = ref('')
// 当前用于请求和高亮的搜索词。选中后要清空，避免员工姓名继续被当作搜索条件或高亮词
const searchKeyword = ref('')
const searchLoading = ref(false)
const userList = ref([])

let searchTimer = null

const clearSelectedUser = () => {
  if (!selectedValue.value) return
  selectedValue.value = ''
  emits('selectUser', null)
}

const getUserList = async (nameLike = '') => {
  searchLoading.value = true
  const params = {
    pageSize: 20,
    page: 1,
    nameLike: nameLike.trim()
  }
  if (!props.isReview) {
    params.assistantAlias = 'meeting'
  }
  if (props.departmentId) {
    params.departmentId = props.departmentId
  }

  try {
    const res = await geuUserList(params)
    userList.value = res.result?.userList || []
  } catch (error) {
    userList.value = []
  } finally {
    searchLoading.value = false
  }
}

const handleOpen = () => {
  const containerWidth = containerRef.value?.getBoundingClientRect().width
  if (containerWidth) popoverWidth.value = containerWidth
  visible.value = true
}

const handleInputClick = () => {
  handleOpen()
  getUserList(searchKeyword.value)
}

const handleSearchInput = (value) => {
  handleOpen()
  searchKeyword.value = value.trim()
  clearSelectedUser()
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    getUserList(searchKeyword.value)
  }, 500)
}

const handleClearSearch = () => {
  inputValue.value = ''
  searchKeyword.value = ''
  clearSelectedUser()
  clearTimeout(searchTimer)
  getUserList()
}

const handleSelectUser = (item) => {
  inputValue.value = item.name
  searchKeyword.value = ''
  selectedValue.value = item.userId
  emits('selectUser', item)
  visible.value = false
}

const highlightName = (name = '') => {
  const keyword = searchKeyword.value
  const matchIndex = keyword ? name.indexOf(keyword) : -1
  if (matchIndex === -1) {
    return [{ text: name, highlighted: false }]
  }

  return [
    { text: name.slice(0, matchIndex), highlighted: false },
    { text: name.slice(matchIndex, matchIndex + keyword.length), highlighted: true },
    { text: name.slice(matchIndex + keyword.length), highlighted: false }
  ].filter((part) => part.text)
}

onBeforeUnmount(() => {
  clearTimeout(searchTimer)
})

defineExpose({
  visible
})
</script>

<style lang="scss">
.shared-user-select__popper.el-popper {
  height: 260px;
  padding: 2px;
  overflow: hidden;
  box-sizing: border-box;
  border: none;
  border-radius: 8px;
}

.shared-user-select__dropdown {
  width: 100%;
  height: 256px;
}

.shared-user-select__option {
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  box-sizing: border-box;
  gap: 8px;
  color: #323233;
  cursor: pointer;
  font-family:
    PingFang SC,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;

  &:hover,
  &:focus-visible {
    background: #f2f3f5;
    outline: none;
  }
}

.shared-user-select__avatar {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  object-fit: cover;
}

.shared-user-select__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  mark {
    padding: 0;
    background: transparent;
    color: #7a52f4;
  }
}

.shared-user-select__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 256px;
  color: #c8c9cc;
  font-family:
    PingFang SC,
    sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
}
</style>
